import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiError, fmtR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { ScanLine, ShieldCheck, ShieldAlert, RotateCw, ArrowLeft, KeyboardIcon, CheckCircle2 } from "lucide-react";

export default function Scanner() {
    const [stage, setStage] = useState("idle"); // idle | scan | result | success
    const [voucher, setVoucher] = useState(null);
    const [coupon, setCoupon] = useState(null);
    const [error, setError] = useState("");
    const [manualSerial, setManualSerial] = useState("");
    const [showManual, setShowManual] = useState(false);
    const [branches, setBranches] = useState([]);
    const [branchId, setBranchId] = useState("");
    const [receipt, setReceipt] = useState("");
    const [amount, setAmount] = useState("");
    const [linePicks, setLinePicks] = useState({}); // { lineId: qty }
    const [submitting, setSubmitting] = useState(false);
    const [successData, setSuccessData] = useState(null);

    const scannerRef = useRef(null);
    const startedRef = useRef(false);

    useEffect(() => {
        api.get("/public/branches").then((r) => setBranches(r.data)).catch(() => setBranches([]));
    }, []);

    useEffect(() => {
        if (stage !== "scan") return;
        let html5;
        const start = async () => {
            try {
                html5 = new Html5Qrcode("qr-reader");
                scannerRef.current = html5;
                await html5.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 260, height: 260 } },
                    (decoded) => {
                        if (startedRef.current) return;
                        startedRef.current = true;
                        handleSerial(decoded);
                    },
                    () => {}
                );
            } catch (e) {
                console.warn("Camera start failed:", e);
                setShowManual(true);
            }
        };
        startedRef.current = false;
        start();
        return () => {
            if (scannerRef.current) {
                scannerRef.current.stop().then(() => scannerRef.current.clear()).catch(() => {});
                scannerRef.current = null;
            }
        };
    }, [stage]);

    const handleSerial = async (serial) => {
        setError("");
        try {
            if (scannerRef.current) {
                await scannerRef.current.stop().catch(() => {});
                scannerRef.current = null;
            }
        } catch (_) {}
        try {
            const { data } = await api.get(`/public/scan/${encodeURIComponent(serial.trim())}`);
            if (data.kind === "coupon") {
                setCoupon(data);
                setVoucher(null);
            } else {
                setVoucher(data);
                setCoupon(null);
            }
            setAmount("");
            setLinePicks({});
            setReceipt("");
            setBranchId("");
            setStage("result");
        } catch (err) {
            setError(formatApiError(err));
            startedRef.current = false;
            setStage("idle");
        }
    };

    const onManualSubmit = (e) => {
        e.preventDefault();
        if (!manualSerial.trim()) return;
        handleSerial(manualSerial);
    };

    const onRedeem = async (e) => {
        e.preventDefault();
        if (!branchId) { toast.error("Select a branch"); return; }
        if (!receipt.trim()) { toast.error("Receipt number required"); return; }
        setSubmitting(true);
        try {
            if (coupon) {
                const body = { receipt_number: receipt.trim(), branch_id: branchId };
                const { data } = await api.post(`/public/coupon/${encodeURIComponent(coupon.code)}/redeem`, body);
                setSuccessData({ ...data, kind: "coupon" });
                setStage("success");
                toast.success("Coupon recorded");
                return;
            }
            const body = { receipt_number: receipt.trim(), branch_id: branchId };
            if (voucher.type === "value") {
                body.amount = parseFloat(amount);
                if (!(body.amount > 0)) { toast.error("Enter amount used"); setSubmitting(false); return; }
            } else {
                const items = Object.entries(linePicks)
                    .map(([line_id, qty]) => ({ line_id, qty: parseInt(qty, 10) || 0 }))
                    .filter((x) => x.qty > 0);
                if (items.length === 0) {
                    toast.error("Tick at least one item to redeem");
                    setSubmitting(false);
                    return;
                }
                body.items = items;
            }
            const { data } = await api.post(`/public/voucher/${encodeURIComponent(voucher.serial)}/redeem`, body);
            setSuccessData({ ...data, kind: "voucher" });
            setStage("success");
            toast.success("Voucher redeemed");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setVoucher(null);
        setCoupon(null);
        setSuccessData(null);
        setManualSerial("");
        setError("");
        setShowManual(false);
        setStage("idle");
    };

    return (
        <div className="min-h-screen bg-crema flex flex-col" data-testid="scanner-page">
            <header className="px-5 py-4 flex items-center justify-between border-b border-[#E5E0D8] bg-white">
                <Logo size={32} />
                <a href="/login" className="text-xs text-[#7A726D] hover:text-espresso" data-testid="admin-link">Admin →</a>
            </header>

            <main className="flex-1 flex flex-col">
                {stage === "idle" && (
                    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center" data-testid="scanner-idle">
                        <img src="/bc-bull.png" alt="Brothers Coffee Roastery" className="w-28 h-auto mb-6" />
                        <div className="text-xs tracking-widest text-[#7A726D] uppercase">Voucher Redemption</div>
                        <h1 className="font-display text-4xl sm:text-5xl font-bold text-espresso mt-2 tracking-tight max-w-md">
                            Ready to scan a voucher?
                        </h1>
                        <p className="text-sm text-[#7A726D] mt-3 max-w-md">
                            Tap below to open your camera and point at the customer’s QR code.
                            You can also enter the serial number manually.
                        </p>
                        <Button
                            onClick={() => { setError(""); setStage("scan"); }}
                            data-testid="start-scan-button"
                            className="mt-8 bg-bc-orange hover:bg-[#cf6800] rounded-sm h-14 px-10 font-display text-lg tracking-wide text-white"
                        >
                            <ScanLine className="w-5 h-5 mr-2" /> Scan QR code
                        </Button>
                        <button
                            onClick={() => { setShowManual(true); setStage("scan"); }}
                            data-testid="idle-manual-button"
                            className="mt-4 flex items-center gap-2 text-sm text-espresso/80 hover:text-espresso"
                        >
                            <KeyboardIcon className="w-4 h-4" strokeWidth={1.5} /> Enter serial manually
                        </button>
                        {error && (
                            <div data-testid="idle-error" className="mt-6 text-sm text-[#B24A45] border border-[#B24A45]/30 bg-[#B24A45]/5 px-3 py-2 rounded-sm max-w-md">
                                {error}
                            </div>
                        )}
                    </div>
                )}

                {stage === "scan" && (
                    <>
                        <div className="px-5 pt-6 pb-4 text-center">
                            <button
                                onClick={reset}
                                data-testid="cancel-scan-button"
                                className="flex items-center gap-1 text-sm text-[#7A726D] mb-3 mx-auto"
                            >
                                <ArrowLeft className="w-4 h-4" /> Cancel
                            </button>
                            <div className="text-xs tracking-widest text-[#7A726D] uppercase">Scanner</div>
                            <h1 className="font-display text-3xl sm:text-4xl font-bold text-espresso mt-1 tracking-tight">
                                Point at the QR code
                            </h1>
                            <p className="text-sm text-[#7A726D] mt-2 max-w-md mx-auto">
                                Allow camera access if prompted. Hold steady — it scans automatically.
                            </p>
                        </div>

                        <div className="px-5 flex-1 flex flex-col items-center">
                            <div id="qr-reader" data-testid="qr-reader" className="w-full max-w-md rounded-sm border border-[#E5E0D8] bg-black" style={{ minHeight: 320 }} />
                            {error && (
                                <div data-testid="scan-error" className="mt-3 text-sm text-[#B24A45] border border-[#B24A45]/30 bg-[#B24A45]/5 px-3 py-2 rounded-sm max-w-md w-full">
                                    {error}
                                </div>
                            )}
                            <button
                                onClick={() => setShowManual((s) => !s)}
                                data-testid="toggle-manual-button"
                                className="mt-4 flex items-center gap-2 text-sm text-[#C48464] hover:text-espresso"
                            >
                                <KeyboardIcon className="w-4 h-4" strokeWidth={1.5} /> Enter serial manually
                            </button>
                            {showManual && (
                                <form onSubmit={onManualSubmit} className="mt-3 flex gap-2 w-full max-w-md" data-testid="manual-form">
                                    <Input
                                        value={manualSerial}
                                        onChange={(e) => setManualSerial(e.target.value)}
                                        placeholder="BC-XXXXXX"
                                        className="rounded-sm border-[#E5E0D8] uppercase"
                                        data-testid="manual-serial-input"
                                    />
                                    <Button type="submit" className="bg-espresso hover:bg-[#2c1a17] rounded-sm" data-testid="manual-submit-button">
                                        <ScanLine className="w-4 h-4" />
                                    </Button>
                                </form>
                            )}
                        </div>
                    </>
                )}

                {stage === "result" && coupon && (
                    <div className="px-5 py-6 max-w-md mx-auto w-full" data-testid="coupon-result">
                        <div className="flex items-center justify-between mb-4">
                            <button onClick={reset} className="flex items-center gap-1 text-sm text-[#7A726D] hover:text-espresso" data-testid="back-button-coupon">
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            <Button onClick={reset} variant="outline" className="rounded-sm border-[#E5E0D8] text-espresso h-9" data-testid="close-coupon-button">
                                Close
                            </Button>
                        </div>

                        {(() => {
                            const tone = {
                                active:   { bg: "#4A6D52", label: "Active",   icon: ShieldCheck },
                                redeemed: { bg: "#C48464", label: "Fully used", icon: ShieldAlert },
                                expired:  { bg: "#7A726D", label: "Expired",  icon: ShieldAlert },
                                void:     { bg: "#B24A45", label: "Void",     icon: ShieldAlert },
                            }[coupon.status] || { bg: "#7A726D", label: coupon.status, icon: ShieldAlert };
                            const Icon = tone.icon;
                            return (
                                <div className="rounded-sm text-white p-6 mb-4 shadow-sm" style={{ backgroundColor: tone.bg }} data-testid={`coupon-status-banner-${coupon.status}`}>
                                    <div className="flex items-center gap-3">
                                        <Icon className="w-9 h-9" strokeWidth={1.5} />
                                        <div>
                                            <div className="text-[10px] tracking-[0.25em] uppercase opacity-80">Coupon</div>
                                            <div className="font-display text-4xl font-bold tracking-tight leading-none mt-0.5">{tone.label}</div>
                                        </div>
                                    </div>
                                    {coupon.status === "expired" && (
                                        <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                                            Expired on <strong>{coupon.expiry_date?.slice(0, 10)}</strong>
                                        </div>
                                    )}
                                    {coupon.status === "redeemed" && (
                                        <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                                            All uses consumed.
                                        </div>
                                    )}
                                    {coupon.status === "void" && (
                                        <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                                            Voided by an administrator.
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                            <CardContent className="p-6">
                                <div className="mb-4">
                                    <div className="text-xs tracking-widest text-[#7A726D] uppercase">Discount coupon</div>
                                    <div className="font-display text-2xl font-bold text-espresso mt-1" data-testid="coupon-name">{coupon.name}</div>
                                    <div className="text-xs text-[#7A726D] mt-1 font-mono">{coupon.code}</div>
                                </div>

                                <div className="bg-[#F3EFEA] p-4 rounded-sm mb-4">
                                    <div className="text-xs text-[#7A726D] uppercase">Discount</div>
                                    <div className="font-display text-4xl font-bold text-espresso tracking-tight" data-testid="coupon-discount">
                                        {coupon.discount_type === "percent" ? `${coupon.discount_value}%` : fmtR(coupon.discount_value)}
                                        <span className="text-base font-normal text-[#7A726D] ml-1">off</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                                    <div>
                                        <div className="text-xs text-[#7A726D] uppercase">Uses left</div>
                                        <div className="font-medium text-espresso">{coupon.uses_remaining} / {coupon.max_uses}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-[#7A726D] uppercase">Expires</div>
                                        <div className="font-medium text-espresso">{coupon.expiry_date ? coupon.expiry_date.slice(0, 10) : "—"}</div>
                                    </div>
                                </div>

                                {coupon.restrictions && (
                                    <div className="mb-4 text-sm border-l-2 border-[#C48464] pl-3 text-[#3E2723]">
                                        <div className="text-xs text-[#7A726D] uppercase mb-0.5">Restrictions</div>
                                        {coupon.restrictions}
                                    </div>
                                )}

                                {coupon.status === "active" && (
                                    <form onSubmit={onRedeem} className="space-y-3 pt-4 border-t border-[#E5E0D8]" data-testid="coupon-redeem-form">
                                        <div className="text-xs tracking-widest text-[#7A726D] uppercase">Record use</div>
                                        <div>
                                            <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Branch</Label>
                                            <Select value={branchId} onValueChange={setBranchId}>
                                                <SelectTrigger className="mt-1 rounded-sm border-[#E5E0D8]" data-testid="coupon-branch-select">
                                                    <SelectValue placeholder="Select branch" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {(() => {
                                                        const allowedIds = coupon.allowed_branch_ids || [];
                                                        const list = allowedIds.length > 0
                                                            ? branches.filter((b) => allowedIds.includes(b.id))
                                                            : branches;
                                                        if (list.length === 0) return <div className="px-3 py-2 text-xs text-[#7A726D]">No allowed branches</div>;
                                                        return list.map((b) => (
                                                            <SelectItem key={b.id} value={b.id} data-testid={`coupon-branch-option-${b.id}`}>{b.name}</SelectItem>
                                                        ));
                                                    })()}
                                                </SelectContent>
                                            </Select>
                                            {coupon.allowed_branches && coupon.allowed_branches.length > 0 && (
                                                <div className="text-xs text-[#7A726D] mt-1">
                                                    Redeemable at: {coupon.allowed_branches.map((b) => b.name).join(", ")}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Loyverse receipt number</Label>
                                            <Input value={receipt} onChange={(e) => setReceipt(e.target.value)} required placeholder="e.g. 1234" className="mt-1 rounded-sm border-[#E5E0D8]" data-testid="coupon-receipt-input"/>
                                        </div>
                                        <Button type="submit" disabled={submitting} data-testid="coupon-redeem-submit-button" className="w-full bg-espresso hover:bg-[#2c1a17] rounded-sm h-11 font-display tracking-wide">
                                            {submitting ? "Processing…" : "Record coupon use"}
                                        </Button>
                                        <button type="button" onClick={reset} data-testid="coupon-cancel-button" className="w-full text-sm text-[#7A726D] hover:text-espresso py-2">
                                            Cancel — view only, don't record
                                        </button>
                                    </form>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {stage === "result" && voucher && (
                    <div className="px-5 py-6 max-w-md mx-auto w-full" data-testid="voucher-result">
                        <div className="flex items-center justify-between mb-4">
                            <button onClick={reset} className="flex items-center gap-1 text-sm text-[#7A726D] hover:text-espresso" data-testid="back-button">
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            <Button
                                onClick={reset}
                                variant="outline"
                                className="rounded-sm border-[#E5E0D8] text-espresso h-9"
                                data-testid="close-result-button"
                            >
                                Close
                            </Button>
                        </div>

                        {(() => {
                            const tone = {
                                active:   { bg: "#4A6D52", label: "Active",   icon: ShieldCheck },
                                redeemed: { bg: "#C48464", label: "Redeemed", icon: ShieldAlert },
                                expired:  { bg: "#7A726D", label: "Expired",  icon: ShieldAlert },
                                void:     { bg: "#B24A45", label: "Void",     icon: ShieldAlert },
                            }[voucher.status] || { bg: "#7A726D", label: voucher.status, icon: ShieldAlert };
                            const Icon = tone.icon;
                            const lr = voucher.last_redemption;
                            const dateOnly = (s) => (s ? s.slice(0, 10) : "");
                            const dateTime = (s) => (s ? s.slice(0, 16).replace("T", " ") : "");
                            return (
                                <div
                                    className="rounded-sm text-white p-6 mb-4 shadow-sm"
                                    style={{ backgroundColor: tone.bg }}
                                    data-testid={`status-banner-${voucher.status}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Icon className="w-9 h-9" strokeWidth={1.5} />
                                        <div>
                                            <div className="text-[10px] tracking-[0.25em] uppercase opacity-80">Voucher status</div>
                                            <div className="font-display text-4xl font-bold tracking-tight leading-none mt-0.5">{tone.label}</div>
                                        </div>
                                    </div>
                                    {voucher.status === "expired" && (
                                        <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                                            Expired on <strong>{dateOnly(voucher.expiry_date)}</strong>
                                        </div>
                                    )}
                                    {voucher.status === "redeemed" && lr && (
                                        <div className="mt-4 pt-4 border-t border-white/20 text-sm leading-relaxed" data-testid="redeem-history">
                                            Redeemed on <strong>{dateTime(lr.redeemed_at)}</strong>
                                            {lr.branch_name && <> at <strong>{lr.branch_name}</strong></>}
                                            {lr.receipt_number && <> under receipt <strong>#{lr.receipt_number}</strong></>}
                                        </div>
                                    )}
                                    {voucher.status === "redeemed" && !lr && (
                                        <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                                            Fully redeemed — no remaining uses.
                                        </div>
                                    )}
                                    {voucher.status === "void" && lr && (
                                        <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                                            Last activity on <strong>{dateTime(lr.redeemed_at)}</strong>
                                            {lr.branch_name && <> at <strong>{lr.branch_name}</strong></>}
                                        </div>
                                    )}
                                    {voucher.status === "void" && !lr && (
                                        <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                                            This voucher has been voided by an administrator.
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                            <CardContent className="p-6">
                                <div className="mb-4">
                                    <div className="text-xs tracking-widest text-[#7A726D] uppercase">{voucher.type === "value" ? "Value voucher" : "Item voucher"}</div>
                                    <div className="font-display text-2xl font-bold text-espresso mt-1" data-testid="voucher-name">{voucher.name}</div>
                                </div>

                                {voucher.type === "value" ? (
                                    <div className="bg-[#F3EFEA] p-4 rounded-sm mb-4">
                                        <div className="text-xs text-[#7A726D] uppercase">Remaining value</div>
                                        <div className="font-display text-4xl font-bold text-espresso tracking-tight" data-testid="remaining-value">
                                            {fmtR(voucher.remaining_value)}
                                        </div>
                                        <div className="text-xs text-[#7A726D] mt-1">of {fmtR(voucher.value_amount)}</div>
                                    </div>
                                ) : (
                                    <div className="bg-[#F3EFEA] p-4 rounded-sm mb-4" data-testid="bundle-summary">
                                        <div className="text-xs text-[#7A726D] uppercase mb-2">Bundle contents</div>
                                        <ul className="space-y-1.5">
                                            {(voucher.lines || []).map((l) => (
                                                <li key={l.id} className="flex items-baseline justify-between text-sm" data-testid={`line-display-${l.id}`}>
                                                    <span className="text-espresso">
                                                        <strong>{l.qty_remaining}×</strong> {l.name}
                                                    </span>
                                                    <span className="text-[#7A726D] text-xs">@ {fmtR(l.retail_value)} ea</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="mt-3 pt-2 border-t border-[#E5E0D8] text-xs text-[#7A726D] flex justify-between">
                                            <span>Remaining value</span>
                                            <span className="font-bold text-espresso">{fmtR(voucher.remaining_value)} of {fmtR(voucher.value_amount)}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                                    <div>
                                        <div className="text-xs text-[#7A726D] uppercase">Uses left</div>
                                        <div className="font-medium text-espresso" data-testid="uses-remaining">{voucher.uses_remaining} / {voucher.max_uses}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-[#7A726D] uppercase">Expires</div>
                                        <div className="font-medium text-espresso">{voucher.expiry_date ? voucher.expiry_date.slice(0,10) : "—"}</div>
                                    </div>
                                </div>

                                {voucher.restrictions && (
                                    <div className="mb-4 text-sm border-l-2 border-[#C48464] pl-3 text-[#3E2723]">
                                        <div className="text-xs text-[#7A726D] uppercase mb-0.5">Restrictions</div>
                                        {voucher.restrictions}
                                    </div>
                                )}

                                {voucher.status === "active" && (
                                    <form onSubmit={onRedeem} className="space-y-3 pt-4 border-t border-[#E5E0D8]" data-testid="redeem-form">
                                        <div className="text-xs tracking-widest text-[#7A726D] uppercase">Redeem</div>

                                        {voucher.type === "value" ? (
                                            <div>
                                                <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Amount used (R)</Label>
                                                <Input
                                                    type="number" step="0.01" min="0.01" max={voucher.remaining_value}
                                                    value={amount} onChange={(e) => setAmount(e.target.value)} required
                                                    className="mt-1 rounded-sm border-[#E5E0D8]"
                                                    data-testid="amount-input"
                                                    placeholder={`Max ${fmtR(voucher.remaining_value)}`}
                                                />
                                            </div>
                                        ) : (
                                            <div data-testid="line-picker">
                                                <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Items being redeemed</Label>
                                                <div className="mt-1 space-y-1.5">
                                                    {(voucher.lines || []).filter((l) => l.qty_remaining > 0).map((l) => {
                                                        const picked = parseInt(linePicks[l.id] || 0, 10);
                                                        const setPicked = (v) => setLinePicks({ ...linePicks, [l.id]: v });
                                                        return (
                                                            <div key={l.id} className="flex items-center gap-2 border border-[#E5E0D8] rounded-sm p-2 bg-white" data-testid={`pick-row-${l.id}`}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={picked > 0}
                                                                    onChange={(e) => setPicked(e.target.checked ? Math.min(1, l.qty_remaining) : 0)}
                                                                    className="w-5 h-5 accent-[#3E2723]"
                                                                    data-testid={`pick-check-${l.id}`}
                                                                />
                                                                <div className="flex-1">
                                                                    <div className="text-sm font-medium text-espresso">{l.name}</div>
                                                                    <div className="text-xs text-[#7A726D]">{l.qty_remaining} left · {fmtR(l.retail_value)} ea</div>
                                                                </div>
                                                                {picked > 0 && (
                                                                    l.qty_remaining > 1 ? (
                                                                        <div className="flex items-center gap-1" data-testid={`pick-stepper-${l.id}`}>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setPicked(Math.max(1, picked - 1))}
                                                                                disabled={picked <= 1}
                                                                                aria-label="Decrease quantity"
                                                                                className="w-9 h-9 rounded-sm border border-[#E5E0D8] bg-white text-espresso text-lg leading-none flex items-center justify-center disabled:opacity-40 active:bg-[#F7F3EE]"
                                                                                data-testid={`pick-qty-minus-${l.id}`}
                                                                            >−</button>
                                                                            <Input
                                                                                type="number" min="1" max={l.qty_remaining}
                                                                                value={picked}
                                                                                onChange={(e) => setPicked(Math.max(1, Math.min(l.qty_remaining, parseInt(e.target.value, 10) || 1)))}
                                                                                className="w-14 h-9 rounded-sm text-center px-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                                                data-testid={`pick-qty-${l.id}`}
                                                                            />
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setPicked(Math.min(l.qty_remaining, picked + 1))}
                                                                                disabled={picked >= l.qty_remaining}
                                                                                aria-label="Increase quantity"
                                                                                className="w-9 h-9 rounded-sm border border-[#E5E0D8] bg-white text-espresso text-lg leading-none flex items-center justify-center disabled:opacity-40 active:bg-[#F7F3EE]"
                                                                                data-testid={`pick-qty-plus-${l.id}`}
                                                                            >+</button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="w-16 h-9 rounded-sm border border-[#E5E0D8] bg-white flex items-center justify-center text-sm font-medium text-espresso"
                                                                            data-testid={`pick-qty-${l.id}`}>
                                                                            1
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {(() => {
                                                    const total = Object.entries(linePicks).reduce((sum, [lid, qty]) => {
                                                        const ln = (voucher.lines || []).find((x) => x.id === lid);
                                                        return sum + (ln ? (parseInt(qty, 10) || 0) * ln.retail_value : 0);
                                                    }, 0);
                                                    return total > 0 ? (
                                                        <div className="mt-2 text-xs text-[#7A726D] flex justify-between">
                                                            <span>Total being redeemed</span>
                                                            <span className="font-bold text-espresso">{fmtR(total)}</span>
                                                        </div>
                                                    ) : null;
                                                })()}
                                            </div>
                                        )}

                                        <div>
                                            <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Branch</Label>
                                            <Select value={branchId} onValueChange={setBranchId}>
                                                <SelectTrigger className="mt-1 rounded-sm border-[#E5E0D8]" data-testid="branch-select">
                                                    <SelectValue placeholder="Select branch" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {(() => {
                                                        const allowedIds = voucher.allowed_branch_ids || [];
                                                        const list = allowedIds.length > 0
                                                            ? branches.filter((b) => allowedIds.includes(b.id))
                                                            : branches;
                                                        if (list.length === 0) return <div className="px-3 py-2 text-xs text-[#7A726D]">No allowed branches available</div>;
                                                        return list.map((b) => (
                                                            <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.id}`}>{b.name}</SelectItem>
                                                        ));
                                                    })()}
                                                </SelectContent>
                                            </Select>
                                            {voucher.allowed_branches && voucher.allowed_branches.length > 0 && (
                                                <div className="text-xs text-[#7A726D] mt-1">
                                                    Redeemable at: {voucher.allowed_branches.map((b) => b.name).join(", ")}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Loyverse receipt number</Label>
                                            <Input
                                                value={receipt} onChange={(e) => setReceipt(e.target.value)} required
                                                placeholder="e.g. 1234"
                                                className="mt-1 rounded-sm border-[#E5E0D8]"
                                                data-testid="receipt-number-input"
                                            />
                                        </div>

                                        <Button type="submit" disabled={submitting}
                                            data-testid="redeem-submit-button"
                                            className="w-full bg-espresso hover:bg-[#2c1a17] rounded-sm h-11 font-display tracking-wide">
                                            {submitting ? "Processing…" : "Confirm redemption"}
                                        </Button>
                                        <button
                                            type="button"
                                            onClick={reset}
                                            data-testid="cancel-redeem-button"
                                            className="w-full text-sm text-[#7A726D] hover:text-espresso py-2"
                                        >
                                            Cancel — view only, do not redeem
                                        </button>
                                    </form>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {stage === "success" && successData && (
                    <div className="px-5 py-6 max-w-md mx-auto w-full" data-testid="redeem-success">
                        <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                            <CardContent className="p-6 text-center">
                                <div className="mx-auto w-14 h-14 rounded-full bg-sage/10 flex items-center justify-center mb-4">
                                    <CheckCircle2 className="w-8 h-8 text-sage" strokeWidth={1.5} />
                                </div>
                                <h2 className="font-display text-2xl font-bold text-espresso">
                                    {successData.kind === "coupon" ? "Coupon recorded" : "Redeemed"}
                                </h2>
                                <p className="text-sm text-[#7A726D] mt-1">
                                    Receipt #{successData.redemption.receipt_number} at {successData.redemption.branch_name}
                                </p>
                                {successData.kind === "coupon" ? (
                                    <div className="mt-4 p-4 bg-[#F3EFEA] rounded-sm text-left">
                                        <div className="text-xs text-[#7A726D] uppercase">Coupon</div>
                                        <div className="font-display text-xl font-bold text-espresso">{successData.coupon.name}</div>
                                        <div className="text-sm text-[#3E2723] mt-1">
                                            {successData.coupon.discount_type === "percent"
                                                ? `${successData.coupon.discount_value}% off`
                                                : `${fmtR(successData.coupon.discount_value)} off`}
                                            {" "}— uses left: {successData.coupon.uses_remaining}/{successData.coupon.max_uses}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mt-4 p-4 bg-[#F3EFEA] rounded-sm text-left">
                                        <div className="text-xs text-[#7A726D] uppercase">Amount</div>
                                        <div className="font-display text-2xl font-bold text-espresso">{fmtR(successData.redemption.amount_redeemed)}</div>
                                        {successData.redemption.items_detail && successData.redemption.items_detail.length > 0 && (
                                            <ul className="mt-2 space-y-0.5 text-sm text-[#3E2723]">
                                                {successData.redemption.items_detail.map((it, i) => (
                                                    <li key={i}>• {it.qty}× {it.name}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                                {successData.voucher && successData.voucher.type === "value" && successData.voucher.status === "active" && (
                                    <div className="mt-3 text-sm text-[#3E2723]">
                                        Remaining: <span className="font-bold">{fmtR(successData.voucher.remaining_value)}</span>
                                    </div>
                                )}
                                <Button onClick={reset} className="w-full mt-6 bg-espresso hover:bg-[#2c1a17] rounded-sm h-11 font-display tracking-wide" data-testid="scan-next-button">
                                    <RotateCw className="w-4 h-4 mr-2" /> Scan next
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </main>
        </div>
    );
}
